/**
 * Cadastra um evento de cobrança na fatura.
 *
 * Nasce Pendente com prazo — é o caso normal, quem registra está pedindo a ação. Alternável para
 * Concluído, para quem registra o que JÁ executou e não vai voltar depois para marcar.
 *
 * Cancelado não é opção de cadastro: ninguém registra uma ação para dizer que ela foi cancelada
 * antes de existir. O backend recusa, e aqui o select simplesmente não oferece.
 */
import { useCallback, useEffect, useState } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import {
    BillingEventOption, BillingEventStatus, createInvoiceBillingEvent, listBillingEventOptions,
} from '../services/billingApi';
import { todayISO } from '../../../../utils/DateUtils';
import PaymentDateInput from '../../../molecules/payment/PaymentDateInput';
import { InstantDraft, draftInstant, nowDraft, touchDraft } from './completionInstant';

interface Props {
    invoiceRef: string | number;
    onClose: () => void;
    onCreated: () => void;
}

const NewInvoiceBillingEventModal: React.FC<Props> = ({ invoiceRef, onClose, onCreated }) => {
    const [options, setOptions] = useState<BillingEventOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [eventRef, setEventRef] = useState('');
    const [status, setStatus] = useState<BillingEventStatus>('PENDING');
    const [notes, setNotes] = useState('');
    /** pré-carregado com hoje, como pedido na spec — no fuso de quem olha, não em UTC */
    const [deadline, setDeadline] = useState(() => todayISO());
    /** o MESMO campo, quando o status é Concluído: quando a ação foi concluída */
    const [completion, setCompletion] = useState<InstantDraft>(nowDraft);
    const [submitting, setSubmitting] = useState(false);

    const alreadyDone = status === 'COMPLETED';

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setOptions(await listBillingEventOptions());
        } catch (e: any) {
            setError(e?.message || 'Falha ao carregar os tipos de evento');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        setSubmitting(true);
        try {
            await createInvoiceBillingEvent(invoiceRef, {
                billing_event_ref: eventRef,
                status,
                notes: notes.trim() || undefined,
                // Concluído no cadastro não tem prazo a cumprir, mas a coluna não fica nula:
                // acompanha a data de CRIAÇÃO. Quem manda é a tela porque só ela sabe o fuso de
                // quem olha — a data UTC do servidor discordaria da Data Inclusão perto da
                // meia-noite.
                event_date_limit: alreadyDone ? todayISO() : deadline,
                // intocado vai `undefined`, e o backend carimba agora
                completed_at: alreadyDone ? draftInstant(completion) : undefined,
            });
            toast.success('Evento de cobrança cadastrado.');
            onCreated();
        } catch (e: any) {
            toast.error(e?.message || 'Não foi possível cadastrar o evento');
        } finally {
            setSubmitting(false);
        }
    };

    // sem prazo em pendente o backend recusa: o botão diz isso antes da viagem
    const canSubmit = !!eventRef && (alreadyDone || !!deadline);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FaPlus className="text-blue-500" size={16} /> Novo evento de cobrança
                    </h2>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                </div>

                {loading && <p className="text-sm text-gray-500 py-4">Carregando tipos de evento...</p>}

                {!loading && error && (
                    <div className="py-4">
                        <p className="text-sm text-red-600">{error}</p>
                        <button onClick={load} className="mt-2 text-sm text-blue-600 hover:underline">
                            Tentar de novo
                        </button>
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <label className="block text-sm font-medium mb-1" htmlFor="evento-acao">
                            Ação
                        </label>
                        <select
                            id="evento-acao"
                            className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            value={eventRef}
                            onChange={e => setEventRef(e.target.value)}
                        >
                            <option value="">Selecione...</option>
                            {options.map(option => (
                                <option key={option.aliasName} value={option.aliasName}>
                                    {option.name}
                                </option>
                            ))}
                        </select>

                        <label className="block text-sm font-medium mb-1" htmlFor="evento-status">
                            Status
                        </label>
                        <select
                            id="evento-status"
                            className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            value={status}
                            onChange={e => setStatus(e.target.value as BillingEventStatus)}
                        >
                            <option value="PENDING">Pendente</option>
                            <option value="COMPLETED">Concluído</option>
                        </select>

                        {/* O MESMO lugar, dois papéis: pendente tem prazo a cumprir, concluído
                            tem data de conclusão. Trocar o campo em vez de desabilitá-lo evita o
                            campo morto que não explica por que morreu — e a data de conclusão só
                            existe quando há conclusão. */}
                        <label className="block text-sm font-medium mb-1"
                            htmlFor={alreadyDone ? 'evento-conclusao' : 'evento-prazo'}>
                            {alreadyDone ? 'Data Conclusão' : 'Data Limite da Ação'}
                        </label>
                        {alreadyDone ? (
                            <>
                                <PaymentDateInput
                                    id="evento-conclusao"
                                    date={completion.date} time={completion.time}
                                    showTime={completion.showTime}
                                    className="border rounded px-2 py-2 text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                    onChange={next => setCompletion(touchDraft(completion, next))} />
                                <p className="text-xs text-gray-500 mt-1 mb-3">
                                    Deixe como está para registrar o momento atual.
                                </p>
                            </>
                        ) : (
                            <input
                                id="evento-prazo"
                                type="date"
                                className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                                value={deadline}
                                onChange={e => setDeadline(e.target.value)}
                            />
                        )}

                        <label className="block text-sm font-medium mb-1" htmlFor="evento-comentario">
                            Comentário
                        </label>
                        <textarea
                            id="evento-comentario"
                            className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                            rows={2}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Opcional"
                        />

                        <div className="flex gap-2 justify-end">
                            <button onClick={onClose} disabled={submitting}
                                className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                                Voltar
                            </button>
                            <button onClick={submit} disabled={submitting || !canSubmit}
                                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                                {submitting ? 'Salvando...' : 'Cadastrar'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default NewInvoiceBillingEventModal;
